import type { JSX, ReactNode } from 'react';
import { Select, SelectItem, SelectSection } from '@heroui/react';
import type { SelectedItems } from '@heroui/react';

import { THEME_OPTIONS, type ThemeName } from '~/contexts/theme';
import { useTheme } from '~/hooks/useTheme';

/**
 * Theme picker — lets the user choose which named theme is active,
 * independent of light/dark. Options come from THEME_OPTIONS; this
 * component never changes when a theme is added or removed.
 */
export function ThemePicker(): JSX.Element {
  const { setThemeName, themeName } = useTheme();

  // Two-line trigger: a small "Theme" header above the current theme's name.
  // `items` can be empty, so never assume items[0] exists.
  const renderValue = (items: SelectedItems): ReactNode => (
    <span className="flex min-w-0 flex-col items-start justify-center leading-tight">
      <span className="text-tiny text-nav-active-text">Theme</span>
      <span className="truncate text-small font-medium">{items[0]?.textValue ?? ''}</span>
    </span>
  );

  return (
    <div className="min-w-[140px]" data-testid="theme-picker">
      <Select
        aria-label="Select theme"
        selectedKeys={[themeName]}
        onSelectionChange={keys => {
          const selected = Array.from(keys)[0] as ThemeName;
          if (selected && selected !== themeName) {
            setThemeName(selected);
          }
        }}
        renderValue={renderValue}
        classNames={{
          // size="sm" fixes the trigger at h-8, which clips the two-line
          // renderValue content above — let it grow to fit instead.
          trigger: 'h-auto py-1.5',
          // The popover's width isn't derived from the trigger's rendered
          // width, so it needs its own min-width to keep option labels from
          // being clipped inside the open list.
          popoverContent: 'min-w-[160px]',
        }}
        size="sm"
      >
        {/* Section title is a non-selectable heading, never part of the option
            collection — clicking it cannot trigger onSelectionChange. */}
        <SelectSection
          key="theme-options"
          title="Theme"
          classNames={{ heading: 'text-nav-active-text' }}
        >
          {THEME_OPTIONS.map(option => (
            <SelectItem
              key={option.value}
              className={option.value === themeName ? 'bg-primary/10 text-foreground' : ''}
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectSection>
      </Select>
    </div>
  );
}
